<script lang="ts" setup>
import LeButton from "@dsk/components/Button/LeButton.vue";
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import { type Component, computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import LeSuccessModal from "@/components/LeSuccessModal.vue";
import { MatomoHelper } from "@/helpers";
import type { Dictionary, SearchViewType } from "@/types";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

import LeUpsellAccessRequestModal from "./LeUpsellAccessRequestModal.vue";

interface Props {
  dictionary: Dictionary;
  searchViewType: SearchViewType;
  type: "button" | "icon-button" | "text-button";
  trackingContext?: "prefix" | "overlay";
}

const props = withDefaults(defineProps<Props>(), {
  trackingContext: undefined
});

defineOptions({
  inheritAttrs: false
});

const { t } = useI18n();

const buttonComponent = computed<Component | string>(() => {
  switch (props.type) {
    case "button":
      return LeButton;
    case "icon-button":
      return LeIconButton;
    default:
      return "button";
  }
});

const typeClass = computed<string[]>(() => [
  "le-upsell-access-request-modal-state-button",
  `le-upsell-access-request-modal-state-button--${props.type}`
]);

const isSuccessModalOpen = ref<boolean>(false);
const isModalOpen = ref<boolean>(false);

const openModal = () => {
  isModalOpen.value = true;
};

const closeModal = (didSubmit: boolean) => {
  isModalOpen.value = false;

  if (didSubmit) {
    isSuccessModalOpen.value = true;
  }
};

const onClick = () => {
  openModal();

  const context = [props.searchViewType, props.trackingContext].filter(Boolean).join(" ");
  MatomoHelper.sendEvent(
    MatomoEventCategory.DictionaryUpsell,
    MatomoEventAction.ClickGetAccess,
    `Clicked get access on dictionary upselling article [${context}]`
  );
};
</script>

<template>
  <Component :is="buttonComponent" :class="typeClass" v-bind="$attrs" @click="onClick()">
    <slot />
  </Component>

  <LeUpsellAccessRequestModal :dictionary="props.dictionary" :is-open="isModalOpen" @close="closeModal($event)" />
  <LeSuccessModal
    :title-text="t('le-upsell.modal.success.title')"
    :body-text="t('le-upsell.modal.success.text')"
    :confirm-text="t('le-upsell.modal.success.button')"
    :is-open="isSuccessModalOpen"
    @close="isSuccessModalOpen = false"
  />
</template>

<style scoped lang="less">
@import "@dsk/styles/all";

.le-upsell-access-request-modal-state-button {
  &--text-button {
    padding: 0;
    border: 0;
    background: none;
  }
}
</style>
